import type { QueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  currentOrg,
  setCurrentOrg,
  setMoveHandler,
  setReloadRequiredHandler,
  setUnauthorizedHandler,
  type SessionMove,
} from "@/shared/api/client";
import { isReconnecting, subscribeReconnecting } from "@/shared/api/renewal";
import type { MeDto } from "@/shared/api/types";
import { ROUTES } from "@/shared/constants/routes";
import { announceOrgChange, listenForOrgChanges } from "./orgChannel";
import { meQueryOptions } from "./session";

/** The two router calls these handlers make; a data router (browser or memory) has both. */
interface SessionNavigator {
  navigate(to: string, options: { replace: boolean; state?: unknown }): unknown;
}

const MOVE_NOTICE = "nacs-session-move";
const RELOAD_NOTICE = "nacs-reload-required";
/** A notice for a move is dropped if the new organisation has not loaded by then (signed out meanwhile). */
const MOVE_NOTICE_WAIT_MS = 120_000;

/** What the person is told once the new organisation has loaded (plan §5.6 step 4). */
function moveNotice(move: SessionMove, now: string): { title: string; description?: string } {
  if (move.cause === "followed") {
    return { title: `You're now in ${now}.`, description: "The organisation was changed in another tab." };
  }
  if (move.reason === "organisation_deleted") {
    return { title: `An organisation you belonged to has been deleted. You're now in ${now}.` };
  }
  return {
    title: `Your access to ${move.endedOrganisationName ?? "an organisation"} has ended. You're now in ${now}.`,
  };
}

/**
 * The session's side of the app (ADR-0011 addendum 2026-10-04, plans/v2/stay-signed-in.md §5.2, §5.6):
 * what happens when the client says the session ended, moved, or the page is out of date, and when
 * another tab says it moved. Installed once by `App`; returns the teardown.
 */
export function installSessionHandlers(queryClient: QueryClient, router: SessionNavigator): () => void {
  let pendingNotice = 0;

  /** Resolves with the account once `/me` has loaded for the organisation the session is in now. */
  const whenMeLoads = (ticket: number): Promise<MeDto | null> =>
    new Promise((resolve) => {
      const key = meQueryOptions().queryKey;
      const done = (me: MeDto | null) => {
        unsubscribe();
        clearTimeout(timer);
        resolve(me);
      };
      const unsubscribe = queryClient.getQueryCache().subscribe(() => {
        const me = ticket === pendingNotice ? queryClient.getQueryData<MeDto>(key) : null;
        if (me !== undefined) done(me);
      });
      const timer = setTimeout(() => done(null), MOVE_NOTICE_WAIT_MS);
      void queryClient.prefetchQuery(meQueryOptions());
    });

  /**
   * The move handler (plan §5.6). Never replays anything: the client has already refused every request
   * that belonged to the old organisation.
   *  1. Gate at once: with the cache empty, `MeGate` has nothing to render but its skeleton, so no screen
   *     of the old organisation stays usable.
   *  2. Nothing is pinned until `/me` says where the session is.
   *  3. Overview, because the page that was open may not exist in the new organisation.
   *  4. Once `/me` has loaded, the notice — and the other tabs are told (unless they told us).
   */
  const move = (change: SessionMove, announce: boolean): void => {
    queryClient.clear();
    setCurrentOrg(null);
    toast.dismiss();
    void router.navigate(ROUTES.overview, { replace: true });
    const ticket = ++pendingNotice;
    void whenMeLoads(ticket).then((me) => {
      if (!me?.tenant || ticket !== pendingNotice) return;
      // The old organisation's requests were refused on the way out; any toast they raised belongs to
      // a page that is gone.
      toast.dismiss();
      const { title, description } = moveNotice(change, me.tenant.name);
      toast.info(title, { id: MOVE_NOTICE, duration: 12_000, ...(description ? { description } : {}) });
      if (announce) {
        announceOrgChange(
          me.tenant.id,
          change.cause === "moved"
            ? { reason: change.reason, endedOrganisationName: change.endedOrganisationName }
            : undefined,
        );
      }
    });
  };

  // A definite "no" from the renewal: drop every cached row and return to sign-in once, saying why.
  setUnauthorizedHandler((reason) => {
    pendingNotice += 1;
    queryClient.clear();
    // The whole address, filters included, so signing in again returns to exactly this view.
    const from = window.location.pathname + window.location.search;
    void router.navigate(ROUTES.login, { replace: true, state: { from, reason } });
  });
  setMoveHandler((change) => move(change, change.cause === "moved"));
  setReloadRequiredHandler(() => {
    toast.error("This page is out of date", {
      id: RELOAD_NOTICE,
      description: "Reload it to continue.",
      duration: Infinity,
      action: { label: "Reload", onClick: () => window.location.reload() },
    });
  });

  // Another tab moved or switched this browser. A tab that shows no organisation (signed out,
  // onboarding, already gated) or already shows that one has nothing to follow.
  const stopListening = listenForOrgChanges((message) => {
    const pinned = currentOrg();
    if (pinned === null || pinned === message.tenantId) return;
    move(message.move ? { cause: "moved", ...message.move } : { cause: "followed" }, false);
  });

  // Back from "Reconnecting…": whatever failed during the outage is asked again, so the screen recovers
  // by itself (the account gate's failure card included).
  let wasReconnecting = isReconnecting();
  const stopRecovery = subscribeReconnecting(() => {
    const now = isReconnecting();
    if (wasReconnecting && !now) {
      void queryClient.refetchQueries({ type: "active", predicate: (q) => q.state.status === "error" });
    }
    wasReconnecting = now;
  });

  return () => {
    pendingNotice += 1;
    setUnauthorizedHandler(null);
    setMoveHandler(null);
    setReloadRequiredHandler(null);
    stopListening();
    stopRecovery();
  };
}
